import { Injectable, Inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';

import * as Utils from '@shared/core/utils';
import * as Tokens from '@shared/core/tokens';
import * as Services from '@shared/core/services';
import * as actions from '@shared/state/actions';

import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface RequestQueueElement {
    request: HttpRequest<unknown>;
    next: HttpHandler;
    resolve: (value: Observable<HttpEvent<unknown>>) => void;
}

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
    private _isRefreshing = false;
    private _requestQueue: Array<RequestQueueElement> = [];

    constructor(@Inject(Tokens.CONFIG_TOKEN) private _config: OLO.Config, private _store: Store<OLO.State>, private _jwtService: Services.JWTService) {}

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (request.url.includes('refreshToken')) {
            return next.handle(request);
        }

        if (request.url.includes(this._config.api.base)) {
            return new Observable<HttpEvent<unknown>>((observer) => {
                this._handleApiRequest(request, next).subscribe(observer);
            });
        }

        return this._addAppSignatureAndHandle(request, next, false);
    }

    private _handleApiRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const requestWithClientAppKey = this._addClientAppKeyHeader(request);

        return this._jwtService.getCurrentTokens().pipe(
            switchMap((tokens) => {
                if (!tokens.AccessToken) {
                    return this._addAppSignatureAndHandle(requestWithClientAppKey, next);
                }

                return this._addAuthHeadersAndHandle(requestWithClientAppKey, next, tokens).pipe(
                    catchError((error) => {
                        if (error instanceof HttpErrorResponse && error.status === 401) {
                            return this._handle401Error(requestWithClientAppKey, next);
                        }

                        return throwError(error);
                    }),
                );
            }),
        );
    }

    private _addAuthHeadersAndHandle(request: HttpRequest<unknown>, next: HttpHandler, tokens: OLO.DTO.JWTokenObject): Observable<HttpEvent<unknown>> {
        const authReq = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${Utils.HTTP.cleanHeaders(tokens.AccessToken)}`),
        });

        return this._addAppSignatureAndHandle(authReq, next);
    }

    private _handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this._isRefreshing) {
            this._isRefreshing = true;

            return this._refreshToken().pipe(
                switchMap((tokens: OLO.DTO.JWTokenObject) => {
                    this._processQueue(tokens);
                    this._isRefreshing = false;

                    return this._addAuthHeadersAndHandle(request, next, tokens);
                }),
                catchError((error) => {
                    this._clearQueue();
                    this._handleUnauthorizedExeption();
                    this._isRefreshing = false;

                    return throwError(error);
                }),
            );
        } else {
            return new Observable<HttpEvent<unknown>>((observer) => {
                this._requestQueue.push({
                    request: request,
                    next: next,
                    resolve: (val) => val,
                });
            });
        }
    }

    private _refreshToken(): Observable<OLO.DTO.JWTokenObject> {
        return this._jwtService
            .getCurrentTokens()
            .pipe(switchMap((tokens: OLO.DTO.JWTokenObject) => this._jwtService.refreshJWToken(tokens.AccessToken, tokens.RefreshToken, this._config.api.key)));
    }

    private _processQueue(tokens: OLO.DTO.JWTokenObject) {
        this._requestQueue.forEach(({ request, next, resolve }) => {
            resolve(this._addAuthHeadersAndHandle(request, next, tokens));
        });
        this._requestQueue = [];
    }

    private _clearQueue() {
        this._requestQueue.forEach(({ resolve }) => {
            resolve(throwError(new HttpErrorResponse({ status: 401 })));
        });
        this._requestQueue = [];
    }

    private _addAppSignatureAndHandle(request: HttpRequest<unknown>, next: HttpHandler, addClientAppSignature: boolean = true): Observable<HttpEvent<unknown>> {
        const signedReq = addClientAppSignature
            ? request.clone({
                  headers: request.headers.set('clientAppSignature', Utils.HTTP.getClientAppSignatureFromRequest(request, this._config.api.key)),
              })
            : request;

        return next.handle(signedReq).pipe(
            map((event) => {
                this._store.dispatch(actions.AppSettingsSetOnlineStatus({ online: true }));

                return event;
            }),
            catchError((error) => {
                if (error.status === 0) {
                    console.warn('Connection status: Offline!');
                    this._store.dispatch(actions.AppSettingsSetOnlineStatus({ online: false }));
                } else {
                    this._store.dispatch(actions.AppSettingsSetOnlineStatus({ online: true }));
                }

                return throwError(error);
            }),
        );
    }

    private _addClientAppKeyHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
        return request.clone({
            headers: request.headers.set('ClientAppKey', Utils.HTTP.cleanHeaders(this._config.api.key)),
        });
    }

    private _handleUnauthorizedExeption(): void {
        this._store.dispatch(actions.MemberSignOut({ redirect: '/' }));
    }
}
